import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Alert } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  createChangeOrder,
  getSingleProjectById,
} from "../../../../slices/thunk";
import { useSelector, useDispatch } from "react-redux";
import { cleanMoney } from "components/AddMilestoneDialog";
import { toast } from "sonner";
import {
  ClipboardList,
  DollarSign,
  FileText,
  Loader2,
  MapPin,
  Timer,
  UserRound,
  UsersRound,
} from "lucide-react";
import RichEditorTipTab from "ractTable";

const ChangeOrder = ({tog_change_order}:{tog_change_order:any}) => {
  const [selectedState, setSelectedState] = useState("");
  const dispatch: any = useDispatch();
  const project = useSelector((state: any) => state.Contract.project);
  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    status: boolean;
    message: string;
    variant: "success" | "danger" | "warning" | "info";
  } | null>(null);
  const homeownerSectionRef = useRef<HTMLDivElement | null>(null);
  const contractorSectionRef = useRef<HTMLDivElement | null>(null);
  const projectSectionRef = useRef<HTMLDivElement | null>(null);
  const accountingSectionRef = useRef<HTMLDivElement | null>(null);
  const documentSectionRef = useRef<HTMLDivElement | null>(null);
  const descriptionOfWorkRef = useRef<HTMLDivElement | null>(null);
  const documentNameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (notification && notification.status) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);

      // Cleanup the timeout if the component unmounts or notification changes
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleAlertClick = () => {
    setNotification(null);
  };

  const validationSchema = Yup.object().shape({
    additionalHomeownerName: Yup.string(),
    descriptionOfWork: Yup.string().required("Description of work is required"),
    documentName: Yup.string().required("Document name is required"),
    priceForWork: Yup.string().required("Price Of Work is required"),
    additionalTimeToComplete: Yup.string().required(
      "Additional Time To Complete Of Work is required"
    ),
    netChangePrior: Yup.string().required("Net Change Prior is required"),
    adjustedGrossContractPrior: Yup.string().required(
      "Adjusted Gross Contract Prior is required"
    ),
    adjustedGrossContractTotal: Yup.string().required(
      "Adjusted Gross Contract Total  is required"
    ),
  });

  const validation = useFormik({
    initialValues: {
      projectId: project?.p_id || "",
      homeownerName: project?.home_owner
        ? `${project.home_owner.user_first_name} ${project.home_owner.user_last_name}`
        : "",
      additionalHomeownerName:
        project?.contract?.pc_additional_home_owner_name || "",
      contractorName: project?.contractor
        ? `${project.contractor.user_first_name} ${project.contractor.user_last_name}`
        : "",
      projectAddress: project?.p_street_address || "",
      city: project?.p_city || "",
      state: project?.p_state || "",
      zip: project?.p_zip || "",
      totalSalesPrice: Number(project?.p_total_cost) .toLocaleString('en-US', { minimumFractionDigits: 2}) || "",
      descriptionOfWork: project?.contract?.pc_work_description || "",
      priceForWork: cleanMoney(project?.change_orders?.priceForWork ) || "",
      additionalTimeToComplete:
        project?.change_orders?.additionalTimeToComplete || "",
      netChangePrior: project?.change_orders?.netChangePrior || "",
      adjustedGrossContractPrior:
        project?.change_orders?.adjustedGrossContractPrior || "",
      adjustedGrossContractTotal:
        project?.change_orders?.adjustedGrossContractTotal || "",

      homeownerId: project?.p_home_owner_id || "",
      documentName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await dispatch(createChangeOrder(values));
        if (project !== "") {
          dispatch(getSingleProjectById(project.p_id));
        }
        toast.success("Change Order Created Successfully")
       
        setLoading(false);
        tog_change_order()
      } catch (error) {
        setLoading(false);
        // Handle error
        console.error("Error creating contract:", error);
      }
    },
  });
  const sectionCardClass = "rounded-xl border border-gray-200 bg-white p-3 sm:p-4 md:p-5 shadow-sm";
  const fieldShellClass = "rounded-lg border border-gray-200 bg-gray-50 p-3";
  const fieldLabelClass = "text-xs font-semibold uppercase text-gray-500";
  const fieldValueClass = "mt-1 text-sm font-semibold text-gray-900";
  const requiredRemaining = [
    validation.values.descriptionOfWork,
    validation.values.priceForWork,
    validation.values.additionalTimeToComplete,
    validation.values.netChangePrior,
    validation.values.adjustedGrossContractPrior,
    validation.values.adjustedGrossContractTotal,
    validation.values.documentName,
  ].filter((item) => !item).length;
  const setupItems = [
    { label: "Homeowner", ready: Boolean(validation.values.homeownerName), icon: UserRound, ref: homeownerSectionRef },
    { label: "Contractor", ready: Boolean(validation.values.contractorName), icon: UsersRound, ref: contractorSectionRef },
    { label: "Project", ready: Boolean(validation.values.projectAddress), icon: MapPin, ref: projectSectionRef },
    { label: "Accounting", ready: requiredRemaining <= 1, icon: DollarSign, ref: accountingSectionRef, focusRef: descriptionOfWorkRef },
    { label: "Document", ready: Boolean(validation.values.documentName), icon: FileText, ref: documentSectionRef, focusRef: documentNameRef },
  ];
  const focusSection = (
    sectionRef: React.RefObject<HTMLDivElement>,
    focusRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>
  ) => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => {
      const focusTarget = focusRef?.current instanceof HTMLDivElement
        ? focusRef.current.querySelector<HTMLElement>("[contenteditable='true']")
        : focusRef?.current;
      focusTarget?.focus();
    }, 250);
  };
  const getErrorText = (error: unknown) => (typeof error === "string" ? error : "");

  return (
    <React.Fragment>
      {notification && notification.status && (
        <div
          style={{
            position: "absolute",
            top: "5%",
            right: "24px",
            zIndex: 999999999,
          }}
        >
          <Alert variant={notification.variant} onClick={handleAlertClick}>
            {notification.message}
          </Alert>
        </div>
      )}
      {loading ? (
        <div className="tailwind-prefix">
          <div
            className="fixed inset-0 flex items-center justify-center bg-gray-900/35 px-4 backdrop-blur-[1px]"
            style={{ zIndex: 100000 }}
          >
            <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-2xl">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    Creating change order...
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Please wait while the document is prepared.
                  </div>
                </div>
              </div>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-blue-600" />
              </div>
            </div>
          </div>
        </div>
      ) : null}
        <div className="tailwind-prefix">
          <Form
            action="#"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Container fluid className="px-0 px-sm-3">
              <Row className="pb-4">
                <Col>
                  <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          <ClipboardList className="h-3.5 w-3.5" />
                          Change Order
                        </div>
                        <div className="mt-3 text-xl font-semibold text-gray-900 md:text-2xl">
                          Add Change Order Contract
                        </div>
                        <div className="mt-1 max-w-3xl text-sm text-gray-600">
                          Review the prefilled project details, complete the work and accounting summary, then create the change order document.
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="text-xs font-medium uppercase text-gray-500">
                          Required Remaining
                        </div>
                        <div className="mt-1 text-xl font-semibold text-gray-900">
                          {requiredRemaining}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                      {setupItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => focusSection(item.ref, item.focusRef)}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition hover:border-blue-300 hover:bg-blue-50 ${item.ready ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-50 text-gray-600"}`}
                          >
                            <Icon className="h-4 w-4 flex-none" />
                            <span className="font-medium">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 pb-4">
                <Col lg={6}>
                  <div ref={homeownerSectionRef} className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                        <UserRound className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Homeowner Details</div>
                        <div className="text-sm text-gray-600">Primary homeowner is locked from the project. Add an extra homeowner only when needed.</div>
                      </div>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>First and last name</Form.Label>
                      <Form.Control
                        type="text"
                        id="homeownerName"
                        name="homeownerName"
                        disabled
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.homeownerName || ""}
                        isInvalid={
                          validation.touched.homeownerName &&
                          !!validation.errors.homeownerName
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.homeownerName}
                      </Form.Control.Feedback>
                    </Form.Group>
                      </Col>
                      <Col md={6}>
                    <Form.Group>
                      <Form.Label>Additional Homeowner Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="additionalHomeownerName"
                        value={validation.values.additionalHomeownerName || ""}
                        onChange={validation.handleChange}
                      />
                    </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col lg={6}>
                  <div ref={contractorSectionRef} className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                        <UsersRound className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Contractor Details</div>
                        <div className="text-sm text-gray-600">Contractor identity is prefilled from the current project.</div>
                      </div>
                    </div>
                    <Form.Group>
                      <Form.Label>First and last name</Form.Label>
                      <Form.Control
                        type="text"
                        id="contractorName"
                        name="contractorName"
                        value={validation.values.contractorName || ""}
                        disabled
                        onChange={validation.handleChange}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 pb-4">
                <Col lg={7}>
                  <div ref={projectSectionRef} className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                        <MapPin className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Project Address</div>
                        <div className="text-sm text-gray-600">Address details are pulled from the project information.</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className={`${fieldShellClass} md:col-span-3`}>
                        <div className={fieldLabelClass}>Street Address</div>
                        <div className={fieldValueClass}>{validation.values.projectAddress || "-"}</div>
                      </div>
                      <div className={fieldShellClass}>
                        <div className={fieldLabelClass}>City</div>
                        <div className={fieldValueClass}>{validation.values.city || "-"}</div>
                      </div>
                      <div className={fieldShellClass}>
                        <div className={fieldLabelClass}>State</div>
                        <div className={fieldValueClass}>{validation.values.state || "-"}</div>
                      </div>
                      <div className={fieldShellClass}>
                        <div className={fieldLabelClass}>Zip</div>
                        <div className={fieldValueClass}>{validation.values.zip || "-"}</div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={5}>
                  <div className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-green-50 text-green-700">
                        <DollarSign className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Original Contract Value</div>
                        <div className="text-sm text-gray-600">Current project total shown as read-only context.</div>
                      </div>
                    </div>
                    <div className={fieldShellClass}>
                      <div className={fieldLabelClass}>Total Sales Price</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900">{validation.values.totalSalesPrice || "-"}</div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="pb-4">
                <Col>
                  <div ref={accountingSectionRef} className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                        <ClipboardList className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Scope of Work & Accounting Summary</div>
                        <div className="text-sm text-gray-600">Capture what changed, how much it costs, and the updated project timing.</div>
                      </div>
                    </div>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group>
                    <Form.Label>Scope of Work *</Form.Label>
                    <div
                      ref={descriptionOfWorkRef}
                      id="descriptionOfWork"
                      className={`rounded border bg-white p-1 ${validation.touched.descriptionOfWork && validation.errors.descriptionOfWork ? "border-danger" : "border-gray-200"}`}
                    >
                      <RichEditorTipTab
                        value={validation.values.descriptionOfWork || ""}
                        onChange={(content: string) => {
                          validation.setFieldValue("descriptionOfWork", content);
                          validation.setFieldTouched("descriptionOfWork", true, false);
                        }}
                      />
                    </div>
                    {validation.touched.descriptionOfWork && validation.errors.descriptionOfWork ? (
                      <div className="invalid-feedback d-block">
                        {getErrorText(validation.errors.descriptionOfWork)}
                      </div>
                    ) : null}
                  </Form.Group>
                      </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Price for work above *</Form.Label>
                    <Form.Control
                      type="text"
                      id="priceForWork"
                      name="priceForWork"
                      value={validation.values.priceForWork || ""}
                      onChange={(e) => {
                        const { name, value } = e.target;
                  
                        // allow only numbers, comma, dot
                        if (!/^[0-9.,]*$/.test(value)) return;
                  
                        // block more than one dot
                        if ((value.match(/\./g) || []).length > 1) return;
                  
                        // block more than 2 decimals
                        const dotIndex = value.indexOf(".");
                        if (dotIndex !== -1) {
                          const decimals = value.slice(dotIndex + 1);
                          if (decimals.length > 2) return;
                        }
                  
                        validation.handleChange(e);
                      }}
                      onBlur={validation.handleBlur}
                      isInvalid={validation.touched.priceForWork && !!validation.errors.priceForWork}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getErrorText(validation.errors.priceForWork)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Additional time to complete the project *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="additionalTimeToComplete"
                      name="additionalTimeToComplete"
                      value={validation.values.additionalTimeToComplete || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      isInvalid={validation.touched.additionalTimeToComplete && !!validation.errors.additionalTimeToComplete}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getErrorText(validation.errors.additionalTimeToComplete)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      Net change by all prior change orders *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="netChangePrior"
                      name="netChangePrior"
                      value={validation.values.netChangePrior || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      isInvalid={validation.touched.netChangePrior && !!validation.errors.netChangePrior}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getErrorText(validation.errors.netChangePrior)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      Adjusted gross contract total prior to this Change Order *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="adjustedGrossContractPrior"
                      name="adjustedGrossContractPrior"
                      value={validation.values.adjustedGrossContractPrior || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      isInvalid={validation.touched.adjustedGrossContractPrior && !!validation.errors.adjustedGrossContractPrior}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getErrorText(validation.errors.adjustedGrossContractPrior)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      Adjusted gross contract total including this Change Order
                      *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="adjustedGrossContractTotal"
                      name="adjustedGrossContractTotal"
                      value={validation.values.adjustedGrossContractTotal || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      isInvalid={validation.touched.adjustedGrossContractTotal && !!validation.errors.adjustedGrossContractTotal}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getErrorText(validation.errors.adjustedGrossContractTotal)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row className="pb-24">
                <Col>
                  <div ref={documentSectionRef} className={sectionCardClass}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">Document Name *</div>
                        <div className="text-sm text-gray-600">This PDF file name will be shared with all signing parties.</div>
                      </div>
                    </div>
                    <Row>
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Control
                          type="text"
                          id="documentName"
                          name="documentName"
                          ref={documentNameRef}
                          placeholder="Example: Kitchen change order"
                          value={validation.values.documentName || ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          isInvalid={validation.touched.documentName && !!validation.errors.documentName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validation.errors.documentName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  </div>
                </Col>
              </Row>

            </Container>
            <div className="sticky bottom-0 z-20 border-t border-gray-200 bg-white/95 px-3 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur">
              <Container fluid className="px-0 px-sm-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Ready to create the change order?
                    </div>
                    <div className="text-sm text-gray-600">
                      {requiredRemaining > 0
                        ? `${requiredRemaining} required item(s) remaining`
                        : "All required fields are complete."}
                    </div>
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                    className="inline-flex w-full min-w-[210px] items-center justify-center md:w-auto"
                  >
                    {loading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="mr-2 h-4 w-4" />
                    )}
                    {loading ? "Creating Document..." : "Create Change Order"}
                  </Button>
                </div>
              </Container>
            </div>
          </Form>
        </div>
    </React.Fragment>
  );
};

export default ChangeOrder;
